import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import './Header.css'; // We'll create this file for header-specific styles
import { HeaderLogo } from './OptimizedLogo';
import { useSmoothScroll } from '../utils/smoothScroll';

const Header = ({ suppressNav = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const { scrollToId, handleHashLinks } = useSmoothScroll();
  const navigate = useNavigate();

  const updateHeaderHeight = useCallback(() => {
    if (!headerRef.current) {
      return;
    }

    const { height } = headerRef.current.getBoundingClientRect();
    setHeaderHeight(height);
    document.documentElement.style.setProperty('--header-offset', `${height}px`);
  }, []);
  
  // Toggle menu function
  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  // Close menu function (for when a menu item is clicked)
  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Handle logo click to navigate home
  const handleLogoClick = () => {
    navigate('/');
  };

  // Handle navigation link clicks with smooth scrolling
  const handleNavClick = (event, targetId) => {
    event.preventDefault();
    closeMenu(); // Close mobile menu if open

    const offsetBuffer = 20;
    const offset = (headerHeight || 105) + offsetBuffer;

    scrollToId(targetId, {
      duration: 800,
      easing: 'easeInOutQuart',
      offset
    });
  };
  
  // Previously we locked body scroll; removed to avoid iOS momentum jitter.
  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      document.body.classList.remove('menu-open');
      document.documentElement.style.removeProperty('--header-offset');
    };
  }, [updateHeaderHeight]);

  useEffect(() => {
    updateHeaderHeight();
  }, [menuOpen, updateHeaderHeight]);

  // Remove scroll listener - no longer needed for static header
  // useEffect removed to eliminate scroll event handling

  // Set up global hash link handling for smooth scrolling
  useEffect(() => {
    const cleanup = handleHashLinks();
    return cleanup;
  }, [handleHashLinks]);
  
  const headerClasses = [
    'header',
    menuOpen ? 'menu-active' : ''
  ].filter(Boolean).join(' ');
  
  return (
    <header 
      className={headerClasses}
      ref={headerRef}
    >
      <div className="header-container">
        <button 
          className="logo"
          onClick={handleLogoClick}
          aria-label="Go to homepage"
        >
          <HeaderLogo 
            alt="Twisted Cloud Solutions" 
            className="logo-image"
          />
        </button>
        
        {!suppressNav && (
          <>
            {/* Hamburger Menu Button */}
            <button 
              className={`hamburger ${menuOpen ? 'active' : ''}`} 
              onClick={toggleMenu}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
            
            {/* Navigation for Desktop and Mobile */}
            <nav className={`main-nav ${menuOpen ? 'open' : ''}`}>
              <ul>
                <li>
                  <a 
                    href="#services" 
                    onClick={(e) => handleNavClick(e, 'services')}
                    aria-label="Navigate to Services section"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a 
                    href="#case-studies" 
                    onClick={(e) => handleNavClick(e, 'case-studies')}
                    aria-label="Navigate to Case Studies section"
                  >
                    Case Studies
                  </a>
                </li>
                <li>
                  <a 
                    href="#about" 
                    onClick={(e) => handleNavClick(e, 'about')}
                    aria-label="Navigate to About section"
                  >
                    About
                  </a>
                </li>
              </ul>
            </nav>
          </>
        )}
      </div>
      
      {/* Overlay for mobile menu - closes menu when clicking outside */}
      {!suppressNav && menuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMenu}></div>
      )}
    </header>
  );
};

export default Header;
